import { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import getIdFromReference from "../../functions/getIdFromReference";

const generateEstimandTableAndEntryArray = (outcome, resourceDictionary) => {
    const endpointReference = outcome.endpoint;
    if (endpointReference) {
        let entryArray = [endpointReference];
        let endpointFoi = getIdFromReference(endpointReference);
        let endpointResource = resourceDictionary[endpointFoi];
        if (!endpointResource) {
            endpointResource = { "title": endpointReference.display };
        }
        let endpointName = outcome.name || endpointResource.shortTitle || endpointResource.title || endpointResource.name;
        let endpointSummary = "<p>" + endpointName + "</p>";
        if (endpointResource.title && endpointResource.title !== endpointName) {
            endpointSummary += "<p>Endpoint title: " + endpointResource.title + "</p>";
        }
        if (endpointResource.description) {
            endpointSummary += "<p>Endpoint description: " + endpointResource.description + "</p>";
        }
        if (endpointResource.definition?.concept) {
            endpointSummary += "<p>Endpoint definition: " + simpleDisplayStringFromFHIRCodeableConcept(endpointResource.definition.concept) + "</p>";
        }
        if (endpointResource.definition?.reference) {
            endpointSummary += "<p>Endpoint definition: " + displayStringFromFHIRReference(endpointResource.definition.reference) + "</p>";
        }
        if (outcome.description) {
            endpointSummary += "<p>Outcome description: " + outcome.description + "</p>";
        }
        let populationSummary = "";
        if (outcome.population) {
            let populationFoi = getIdFromReference(outcome.population);
            entryArray.push(outcome.population);
            populationSummary = resourceDictionary[populationFoi]?.description || outcome.population.display;
        }
        let interventionSummary = "";
        if (outcome.intervention) {
            let interventionFoi = getIdFromReference(outcome.intervention);
            entryArray.push(outcome.intervention);
            interventionSummary = resourceDictionary[interventionFoi]?.description || outcome.intervention.display;
        }
        let comparatorSummary = "";
        if (outcome.comparator) {
            let comparatorFoi = getIdFromReference(outcome.comparator);
            entryArray.push(outcome.comparator);
            comparatorSummary = resourceDictionary[comparatorFoi]?.description || outcome.comparator.display;
        }
        let metricSummary = "";
        if (outcome.summaryMeasure) {
            metricSummary = simpleDisplayStringFromFHIRCodeableConcept(outcome.summaryMeasure);
        }
        let eventTableRows = "";
        if (outcome.eventHandling?.length > 0) {
            eventTableRows = "<tr><td>Other Intercurrent Event</td><td>Strategy</td></tr>";
            for (const issue of outcome.eventHandling) {
                const event = simpleDisplayStringFromFHIRCodeableConcept(issue.event) || "";
                const group = simpleDisplayStringFromFHIRCodeableConcept(issue.group);
                const handling = simpleDisplayStringFromFHIRCodeableConcept(issue.handling) || "";
                let strategy = "";
                if (group) {
                    strategy = "for " + group + ": ";
                }
                strategy += handling;
                if (issue.description) {
                    strategy += " " + issue.description;
                }
                eventTableRows += "<tr><td>" + event + "</td><td>" + strategy + "</td></tr>";
            }
        }
        let estimandTable = "<table><tbody><tr><th>Estimand Characteristic</th><th>Description</th></tr>";
        if (populationSummary) {
            estimandTable += "<tr><td>Population</td><td>" + populationSummary + "</td></tr>";
        }
        if (interventionSummary) {
            estimandTable += "<tr><td>Treatment</td><td>" + interventionSummary + "</td></tr>";
        }
        if (comparatorSummary) {
            estimandTable += "<tr><td>Treatment</td><td>" + comparatorSummary + "</td></tr>";
        }
        if (endpointSummary) {
            estimandTable += "<tr><td>Endpoint</td><td>" + endpointSummary + "</td></tr>";
        }
        if (metricSummary) {
            estimandTable += "<tr><td>Population-level Summary</td><td>" + metricSummary + "</td></tr>";
        }
        if (eventTableRows) {
            estimandTable += eventTableRows;
        }
        estimandTable += "</tbody></table>";
        return [endpointReference, estimandTable, entryArray];
    } else {
        return [null, null, null];
    }
}

const generateM11Section3Composition = (researchStudyJson, resourceDictionary) => {
    if (!researchStudyJson || !researchStudyJson.id) {
        return null;
    }
    let compositionJson = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-03"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218526",
                        "display": "ICH M11 Protocol Section 3 TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS"
                    }
                ],
                "text": "section3-estimands"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 3 (Trial Objectives and Associated Estimands) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "3 TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218526",
                            "display": "ICH M11 Protocol Section 3 TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS"
                        }
                    ],
                    "text": "section3-estimands"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No text is intended here (heading only).]</p></div>"
                },
                "section": [
                    {
                        "title": "3.1 Primary Objective(s) and Associated Estimand(s)",
                        "code": {
                            "text": "3.1-Primary-Objective(s)-and-Associated-Estimand(s)",
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218527",
                                    "display": "ICH M11 Protocol Section 3.1 Primary Objective(s) and Associated Estimand(s)"
                                }
                            ]
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No text is intended here (heading only).]</p></div>"
                        },
                        "section": []
                    },
                    {
                        "title": "3.2 Secondary Objective(s) and Associated Estimand(s)",
                        "code": {
                            "text": "3.2-Secondary-Objective(s)-and-Associated-Estimand(s)",
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218529",
                                    "display": "ICH M11 Protocol Section 3.2 Secondary Objective(s) and Associated Estimand(s)"
                                }
                            ]
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No text is intended here (heading only).]</p></div>"
                        },
                        "section": []
                    },
                    {
                        "title": "3.3 Exploratory Objective(s)",
                        "code": {
                            "text": "3.3-Exploratory-Objective(s)",
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218531",
                                    "display": "ICH M11 Protocol Section 3.3 Exploratory Objective(s)"
                                }
                            ]
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No text is intended here (heading only).]</p></div>"
                        },
                        "section": []
                    }
                ]
            }
        ]
    };

    let primaryObjectives = [];
    let secondaryObjectives = [];
    let exploratoryObjectives = [];

    if (Array.isArray(researchStudyJson.objective) && researchStudyJson.objective.length > 0) {
        for (const objective of researchStudyJson.objective) {
            let objectiveType = objective.type?.coding?.[0]?.code;
            if (objectiveType === "primary") {
                primaryObjectives.push(objective);
            } else if (objectiveType === "secondary") {
                secondaryObjectives.push(objective);
            } else if (objectiveType === "exploratory") {
                exploratoryObjectives.push(objective);
            }
        }
    }

    if (primaryObjectives.length > 0) {
        for (const index in primaryObjectives) {
            const objective = primaryObjectives[index];
            let objectiveNarrative = "";
            if (objective.name) {
                objectiveNarrative += "<p>Primary objective label: " + objective.name + "</p>";
            }
            if (objective.description) {
                objectiveNarrative += "<p>Primary objective description: " + objective.description + "</p>";
            }
            if (Array.isArray(objective.outcomeMeasure) && objective.outcomeMeasure.length > 0) {
                for (const outcome of objective.outcomeMeasure) {
                    let [endpointReference, estimandTable, entryArray] = generateEstimandTableAndEntryArray(outcome, resourceDictionary);
                    objectiveNarrative += estimandTable;
                }
            }
            let primaryObjectiveSection = {
                "title": "3.1.1 Primary Objective " + (index + 1),
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218528",
                            "display": "ICH M11 Protocol Section 3.1.1 Primary Objective"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + objectiveNarrative + "</div>",
                }
            };
            compositionJson.section[0].section[0].section.push(primaryObjectiveSection);
        }
    } else {
        alert("There are no primary objectives detected and at least one is required.");
    }
    if (secondaryObjectives.length > 0) {
        for (const index in secondaryObjectives) {
            const objective = secondaryObjectives[index];
            let objectiveNarrative = "";
            if (objective.name) {
                objectiveNarrative += "<p>Secondary objective label: " + objective.name + "</p>";
            }
            if (objective.description) {
                objectiveNarrative += "<p>Secondary objective description: " + objective.description + "</p>";
            }
            if (Array.isArray(objective.outcomeMeasure) && objective.outcomeMeasure.length > 0) {
                for (const outcome of objective.outcomeMeasure) {
                    let [endpointReference, estimandTable, entryArray] = generateEstimandTableAndEntryArray(outcome, resourceDictionary);
                    objectiveNarrative += estimandTable;
                }
            }
            let secondaryObjectiveSection = {
                "title": "3.2.1 Secondary Objective " + (index + 1),
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218530",
                            "display": "ICH M11 Protocol Section 3.2.1 Secondary Objective"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + objectiveNarrative + "</div>",
                }
            };
            compositionJson.section[0].section[1].section.push(secondaryObjectiveSection);
        }
    } else {
        compositionJson.section[0].section[1].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not applicable</p></div>"
        };
    }
    if (exploratoryObjectives.length > 0) {
        for (const index in exploratoryObjectives) {
            const objective = exploratoryObjectives[index];
            let objectiveNarrative = "";
            if (objective.name) {
                objectiveNarrative += "<p>Exploratory objective label: " + objective.name + "</p>";
            }
            if (objective.description) {
                objectiveNarrative += "<p>Exploratory objective description: " + objective.description + "</p>";
            }
            if (Array.isArray(objective.outcomeMeasure) && objective.outcomeMeasure.length > 0) {
                for (const outcome of objective.outcomeMeasure) {
                    let [endpointReference, estimandTable, entryArray] = generateEstimandTableAndEntryArray(outcome, resourceDictionary);
                    objectiveNarrative += estimandTable;
                }
            }
            let exploratoryObjectiveSection = {
                "title": "3.3.1 Exploratory Objective " + (index + 1),
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218532",
                            "display": "ICH M11 Protocol Section 3.3.1 Exploratory Objective"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + objectiveNarrative + "</div>",
                }
            };
            compositionJson.section[0].section[2].section.push(exploratoryObjectiveSection);
        }
    } else {
        compositionJson.section[0].section[2].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not applicable</p></div>"
        };
    }
    return compositionJson;
};

export default generateM11Section3Composition;